export interface DeliverableMetadata {
  type: string
  title?: string
  [key: string]: string | undefined
}

export interface ParsedDeliverable {
  metadata: DeliverableMetadata
  content: string
}

export interface ParsedDeliverableContent {
  textBefore: string
  deliverables: ParsedDeliverable[]
  textAfter: string
  hasIncompleteTag: boolean
  incompleteDeliverable?: ParsedDeliverable
}

export function parseDeliverablesFromText(
  text: string,
): ParsedDeliverableContent {
  const deliverableRegex =
    /<deliverable\s+type="([^"]+)"(?:\s+title="([^"]+)")?>([\s\S]*?)<\/deliverable>/gi
  const deliverables: ParsedDeliverable[] = []
  let match

  const matches: Array<{
    fullMatch: string
    type: string
    title?: string
    content: string
    index: number
  }> = []

  while ((match = deliverableRegex.exec(text)) !== null) {
    matches.push({
      fullMatch: match[0],
      type: match[1].trim(),
      title: match[2]?.trim(),
      content: match[3],
      index: match.index,
    })
  }

  let hasIncompleteTag = false
  let incompleteDeliverable: ParsedDeliverable | undefined

  const lastCompleteBlockEnd =
    matches.length > 0
      ? matches[matches.length - 1].index +
        matches[matches.length - 1].fullMatch.length
      : 0

  const textAfterComplete = text.substring(lastCompleteBlockEnd)
  const incompleteBlockStart = textAfterComplete.indexOf('<deliverable')

  if (incompleteBlockStart !== -1) {
    const incompleteText = textAfterComplete.substring(incompleteBlockStart)
    const hasClosing = incompleteText.includes('</deliverable>')

    if (!hasClosing) {
      let incompleteMatch = incompleteText.match(
        /^<deliverable\s+type="([^"]+)"(?:\s+title="([^"]+)")?>([\s\S]*)$/i,
      )

      if (!incompleteMatch) {
        incompleteMatch = incompleteText.match(
          /^<deliverable\s+type="([^"]*)"?(?:\s+title="([^"]*)"?)?(.*)$/i,
        )

        if (incompleteMatch) {
          const type = incompleteMatch[1]?.trim()
          if (type) {
            hasIncompleteTag = true
            const title = incompleteMatch[2]?.trim()
            // If the tag is not closed, there's no content yet
            const hasOpeningTagClosed = incompleteText.includes('>')
            const rawContent = hasOpeningTagClosed
              ? incompleteText.split('>').slice(1).join('>') || ''
              : ''
            const content = rawContent.startsWith('\n')
              ? rawContent.substring(1)
              : rawContent

            incompleteDeliverable = {
              metadata: {
                type,
                title,
              },
              content,
            }
          }
        }
      } else {
        hasIncompleteTag = true
        const type = incompleteMatch[1].trim()
        const title = incompleteMatch[2]?.trim()
        const rawContent = incompleteMatch[3] || ''
        const content = rawContent.startsWith('\n')
          ? rawContent.substring(1)
          : rawContent

        incompleteDeliverable = {
          metadata: {
            type,
            title,
          },
          content,
        }
      }
    }
  }

  if (matches.length === 0 && !hasIncompleteTag) {
    return {
      textBefore: text,
      deliverables: [],
      textAfter: '',
      hasIncompleteTag: false,
    }
  }

  if (matches.length === 0 && hasIncompleteTag) {
    const incompleteStart = textAfterComplete.indexOf('<deliverable')
    return {
      textBefore: text.substring(0, lastCompleteBlockEnd + incompleteStart),
      deliverables: [],
      textAfter: '',
      hasIncompleteTag: true,
      incompleteDeliverable,
    }
  }

  let textBefore = ''
  let textAfter = ''

  if (matches.length > 0) {
    textBefore = text.substring(0, matches[0].index)

    matches.forEach((match) => {
      deliverables.push({
        metadata: {
          type: match.type,
          title: match.title,
        },
        content: match.content.trim(),
      })
    })

    const lastMatch = matches[matches.length - 1]
    textAfter = text.substring(lastMatch.index + lastMatch.fullMatch.length)

    if (hasIncompleteTag && incompleteBlockStart !== -1) {
      textAfter = textAfterComplete.substring(0, incompleteBlockStart)
    }
  }

  return {
    textBefore,
    deliverables,
    textAfter,
    hasIncompleteTag,
    incompleteDeliverable,
  }
}

export function generateDeliverableId(metadata: DeliverableMetadata): string {
  const timestamp = Date.now()
  const random = Math.random().toString(36).substring(2, 9)
  return `deliverable-${metadata.type}-${timestamp}-${random}`
}
